import z from 'zod'

/**
 * Sodex REST API の共通レスポンスから配列 `data` を取り出す Zod スキーマを生成する
 * `code === 0` でも `data` が省略・null のときは空配列を返す（空結果のレスポンスに対応する）
 * @param itemSchema 配列要素 1 件分のスキーマ
 * @returns パース後に `data` 配列のみを返すスキーマ
 */
export const createSodexArrayEnvelopeSchema = <I extends z.ZodTypeAny>(
  itemSchema: I,
) => {
  const arraySchema = z.array(itemSchema)

  return z
    .object({
      code: z.number(),
      data: z.unknown().optional(),
      error: z.string().optional(),
    })
    .transform((response): z.infer<typeof arraySchema> => {
      if (response.code !== 0) {
        throw new Error(
          response.error ?? `Sodex API error: code ${response.code}`,
        )
      }

      const raw = response.data
      if (raw === undefined || raw === null) {
        return []
      }

      return arraySchema.parse(raw)
    })
}

/**
 * Sodex REST API の共通レスポンスからオブジェクト `data` を取り出す Zod スキーマを生成する
 * `code === 0` でも `data` が省略・null のときは空オブジェクトを渡して `dataSchema` でパースする（`.default()` 付きフィールドで空結果に対応する）
 * @param dataSchema `data` ペイロード用の Zod スキーマ
 * @returns パース後に `data` オブジェクトのみを返すスキーマ
 */
export const createSodexObjectEnvelopeSchema = <D extends z.ZodTypeAny>(
  dataSchema: D,
) => {
  return z
    .object({
      code: z.number(),
      data: z.unknown().optional(),
      error: z.string().optional(),
    })
    .transform((response): z.infer<D> => {
      if (response.code !== 0) {
        throw new Error(
          response.error ?? `Sodex API error: code ${response.code}`,
        )
      }

      const raw = response.data
      if (raw === undefined || raw === null) {
        return dataSchema.parse({})
      }

      return dataSchema.parse(raw)
    })
}
